import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { editGroup } from "formValidations/classification";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import {
  useGetAllClassificationQuery,
  useUpdateClassificationMutation,
} from "store/apis/classifications";
import { BackendError } from "types/utils/error";
import { ClassificationWarningModal } from "./ClassificationWarningModal";

type EditGroupModalProps = {
  isOpen: boolean;
  onClose: () => void;
  groupName: string;
  groupId: string;
};

export const EditGroupModal = ({
  isOpen,
  onClose,
  groupName,
  groupId,
}: EditGroupModalProps) => {
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const warningModal = useModal();
  const [updateClassification, { isLoading }] =
    useUpdateClassificationMutation();
  const { data: classifications = [] } = useGetAllClassificationQuery(
    { entityId },
    { skip: !entityId }
  );

  const handleSave = async (values: { name: string }) => {
    if (values.name === groupName) {
      onClose();
      return;
    }
    warningModal.open();
  };

  const handleConfirmUpdate = async (newName: string) => {
    try {
      await updateClassification({
        entityId,
        payload: { group_id: groupId, name: newName },
      }).unwrap();

      successToast({ message: `Group renamed to "${newName}"` });
      warningModal.close();
      onClose();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Modal.Content asChild>
          <FormikForm
            initialValues={{ name: groupName }}
            onSubmit={handleSave}
            validationSchema={() => editGroup(classifications, groupName)}
            validateOnChange
          >
            {({ submitForm, values }) => (
              <>
                <Modal.Header>
                  <Modal.Title>Edit group</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body>
                  <TextInput
                    name="name"
                    label="Group name"
                    placeholder="Enter group name"
                    required
                  />
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button>Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    onClick={submitForm}
                    type="button"
                  >
                    Save
                  </Button>
                </Modal.FooterButtonGroup>

                {warningModal.isOpen && (
                  <ClassificationWarningModal
                    isOpen={warningModal.isOpen}
                    onClose={warningModal.close}
                    onConfirm={() => handleConfirmUpdate(values.name)}
                    title="Update group name"
                    description="Renaming the group will apply the change to all transactions assigned to it. Please confirm to proceed."
                    isLoading={isLoading}
                  />
                )}
              </>
            )}
          </FormikForm>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
